"use client";

import * as React from "react";
import { useMemo } from "react";
import { Button, message } from "antd";
import { GalleryVerticalEnd, Menu, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

import { NavMain } from "./nav-main";
import { NavUser } from "./nav-user";
import { TeamSwitcher } from "./team-switcher";
import { useAppBranding, useSystemConfig } from "../../Hook";
import { getHiddenFeatureCodes } from "../../Constant";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  useSidebar,
} from "../ui/sidebar";
import { useAuthChecker } from "../../Context";
import { useUserFeaturesQuery } from "../../Services";
import { useClearRuntimeCacheMutation } from "../../Services";
import { NetworkStats } from "./NetworkStats";

interface SubFeature {
  id: string;
  code: string;
  name: string;
  icon?: string;
  is_enabled?: number;
  url?: string;
}

interface Feature {
  id: string;
  feature_id?: number | null;
  feature_code: string;
  feature_name: string;
  feature_icon?: string;
  sub_features?: SubFeature[];
}

const mapFeaturesToSidebarItems = (
  featuresList: Feature[],
  hiddenCodes: string[],
) => {
  const enabledCodes: string[] = [];

  const items = featuresList.map((feature) => {
    const hasSub = feature.sub_features && feature.sub_features.length > 0;

    const category = {
      name: feature.feature_name,
      icon: feature.feature_icon || "tag",
    };

    const features = hasSub
      ? feature.sub_features!.map((sub) => {
          const hidden = hiddenCodes.includes(sub.code);
          if (sub.is_enabled && !hidden) {
            enabledCodes.push(sub.code);
          }
          return {
            id: sub.id,
            label: sub.name,
            icon: sub.icon || "tag",
            navigation: sub.code,
            is_show_navigation: 1,
            is_locked: !sub.is_enabled || hidden ? 1 : 0,
            feature_yt_url: sub.url,
          };
        })
      : [
          {
            id: feature.id,
            label: feature.feature_name,
            icon: feature.feature_icon || "tag",
            navigation: feature.feature_code,
            is_show_navigation: 1,
            is_locked: 0,
            feature_yt_url: undefined,
          },
        ];

    return { category, features };
  });

  localStorage.setItem("enabled_features", JSON.stringify(enabledCodes));
  return items;
};

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { user, navigationPath, isAdmin } = useAuthChecker();
  const { appName } = useAppBranding();
  const { config } = useSystemConfig();
  const queryClient = useQueryClient();
  const clearRuntimeCacheMutation = useClearRuntimeCacheMutation();
  const featuresQuery = useUserFeaturesQuery();
  const { state, isMobile, toggleSidebar } = useSidebar();

  const sidebarTeams = [
    {
      name: appName,
      logo: GalleryVerticalEnd,
      plan: "Enterprise",
    },
  ];

  const sidebarItems = useMemo(() => {
    if (!featuresQuery.data) return [];
    return mapFeaturesToSidebarItems(
      featuresQuery.data,
      getHiddenFeatureCodes(config ?? undefined),
    );
  }, [featuresQuery.data, config]);

  const handleClearCache = () => {
    clearRuntimeCacheMutation.mutate(["api"], {
      onSuccess: (res) => {
        if (res?.success) {
          message.success(res.message || "Application cache cleared.");
          queryClient.invalidateQueries();
        } else {
          message.error(res?.message || "Failed to clear cache.");
        }
      },
      onError: (err: any) => {
        message.error(err?.message || "Failed to clear cache.");
      },
    });
  };

  return (
    <>
      <Sidebar collapsible="icon" {...props}>
        <SidebarHeader>
          <TeamSwitcher teams={sidebarTeams} />
          {state === "expanded" && (
            <div className="px-1.5 py-1">
              <NetworkStats />
            </div>
          )}
        </SidebarHeader>

        <SidebarContent>
          <NavMain items={sidebarItems} navigationPath={navigationPath} />
        </SidebarContent>

        <SidebarFooter>
          <NavUser
            user={user}
            handleClearCache={handleClearCache}
          />
        </SidebarFooter>

        <SidebarRail />
      </Sidebar>

      <div
        className="fixed top-4 z-40 transition-all duration-200 ease-linear"
        style={{
          left: isMobile
            ? "0.75rem"
            : state === "expanded"
              ? "calc(var(--sidebar-width) + 1.5rem)"
              : "3.5rem",
        }}
      >
        <button
          type="button"
          data-sidebar="trigger"
          aria-label="Toggle Sidebar"
          title="Toggle Sidebar"
          onClick={() => toggleSidebar()}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-sidebar-accent/80 text-sidebar-foreground shadow-md ring-1 ring-sidebar-border transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        >
          {state === "expanded" ? (
            <X className="size-4" />
          ) : (
            <Menu className="size-4" />
          )}
        </button>
      </div>
    </>
  );
}
